import React, { useState, useRef, useEffect } from 'react';

const FolderCard = ({ name, count, color, onClick, onRename, onColorChange, onDelete, onArchive, onUnarchive }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Styles configuration
  const styles = {
    blue:    { dot: 'bg-blue-50',    iconBg: 'bg-blue-100',    pickerBg: 'bg-blue-500',    iconText: 'text-blue-600',    hoverBg: 'group-hover:bg-blue-600',    hoverText: 'text-blue-500',    ring: 'ring-blue-500' },
    orange:  { dot: 'bg-orange-50',  iconBg: 'bg-orange-100',  pickerBg: 'bg-orange-500',  iconText: 'text-orange-600',  hoverBg: 'group-hover:bg-orange-500',  hoverText: 'text-orange-500',  ring: 'ring-orange-500' },
    purple:  { dot: 'bg-purple-50',  iconBg: 'bg-purple-100',  pickerBg: 'bg-purple-500',  iconText: 'text-purple-600',  hoverBg: 'group-hover:bg-purple-600',  hoverText: 'text-purple-500',  ring: 'ring-purple-500' },
    emerald: { dot: 'bg-emerald-50', iconBg: 'bg-emerald-100', pickerBg: 'bg-emerald-500', iconText: 'text-emerald-600', hoverBg: 'group-hover:bg-emerald-600', hoverText: 'text-emerald-500', ring: 'ring-emerald-500' },
    green:   { dot: 'bg-emerald-50', iconBg: 'bg-emerald-100', pickerBg: 'bg-emerald-500', iconText: 'text-emerald-600', hoverBg: 'group-hover:bg-emerald-600', hoverText: 'text-emerald-500', ring: 'ring-emerald-500' },
    red:     { dot: 'bg-red-50',     iconBg: 'bg-red-100',     pickerBg: 'bg-red-500',     iconText: 'text-red-600',     hoverBg: 'group-hover:bg-red-600',     hoverText: 'text-red-500',     ring: 'ring-red-500' },
    gray:    { dot: 'bg-gray-50',    iconBg: 'bg-gray-100',    pickerBg: 'bg-gray-500',    iconText: 'text-gray-600',    hoverBg: 'group-hover:bg-gray-600',    hoverText: 'text-gray-500',    ring: 'ring-gray-500' },
  };

  const activeColorKey = color?.toLowerCase() || 'blue';
  const s = styles[activeColorKey] || styles.blue;

  // Available colors for the picker
  const availableColors = ['blue', 'orange', 'purple', 'emerald', 'red', 'gray'];

  const handleMenuClick = (e) => {
    e.stopPropagation(); 
    setIsMenuOpen(!isMenuOpen);
  };

  const handleAction = (e, action) => {
    e.stopPropagation();
    if (action) action();
    setIsMenuOpen(false);
  };

  return (
    <div className="relative group h-full">
      {/* Visual Card Wrapper */}
      <div 
        onClick={onClick} 
        className="relative bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer overflow-hidden block text-left active:scale-95 h-full"
      >
        {/* Decorative Corner Circle */}
        <div className={`absolute -right-4 -top-6 w-24 h-24 ${s.dot} rounded-full group-hover:scale-110 transition-transform duration-500 ease-out`}></div>
        
        <div className="relative z-10 pr-6">
          {/* Icon Box */}
          <div className={`w-10 h-10 rounded-lg ${s.iconBg} ${s.iconText} flex items-center justify-center mb-3 group-hover:bg-white group-hover:shadow-inner transition-all duration-300`}>
            <i className="fa-solid fa-folder"></i>
          </div>
          
          {/* Text */}
          <h4 className="font-bold text-gray-800 text-sm truncate mb-1" title={name}>{name}</h4>
          
          <div className="flex items-center justify-between mt-2">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">{count} Files</p>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center bg-gray-50 group-hover:${s.dot} transition-colors`}>
              <i className={`fa-solid fa-chevron-right text-[8px] text-gray-400 group-hover:${s.iconText} transition-transform group-hover:translate-x-0.5`}></i>
            </div>
          </div>
        </div>
      </div>

      {/* --- MENU BUTTON --- */}
      <div className="absolute top-3 right-3 z-20" ref={menuRef}>
        <button
          onClick={handleMenuClick}
          className={`w-7 h-7 flex items-center justify-center rounded-full bg-transparent hover:bg-white hover:shadow-sm text-gray-400 hover:text-gray-600 transition-all ${isMenuOpen ? 'bg-white shadow-sm text-gray-600' : ''}`}
        >
          <i className="fa-solid fa-ellipsis-vertical text-xs"></i>
        </button>

        {/* --- DROPDOWN MENU --- */}
        {isMenuOpen && (
          <div className="absolute right-0 top-8 w-48 bg-white rounded-xl shadow-xl border border-gray-100 p-2 z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-right">
            
            {/* Rename */}
            {onRename && (
              <button 
                onClick={(e) => handleAction(e, () => onRename && onRename())}
                className="w-full text-left px-3 py-2 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 hover:text-blue-600 flex items-center gap-2 transition-colors"
              >
                <i className="fa-solid fa-pen w-4 text-center"></i> Rename Folder
              </button>
            )}

            {/* Archive Folder */}
            {onArchive && (
              <button 
                onClick={(e) => handleAction(e, () => onArchive && onArchive())}
                className="w-full text-left px-3 py-2 rounded-lg text-xs font-semibold text-blue-600 hover:bg-blue-50 flex items-center gap-2 transition-colors"
              >
                <i className="fa-solid fa-box-archive w-4 text-center text-blue-500"></i> Archive Folder
              </button>
            )}

            {/* Unarchive Folder */}
            {onUnarchive && (
              <button 
                onClick={(e) => handleAction(e, () => onUnarchive && onUnarchive())}
                className="w-full text-left px-3 py-2 rounded-lg text-xs font-semibold text-blue-600 hover:bg-blue-50 flex items-center gap-2 transition-colors"
              >
                <i className="fa-solid fa-box-open w-4 text-center text-blue-500"></i> Unarchive Folder
              </button>
            )}

            {/* ✅ Delete Folder */}
            {onDelete && (
              <button 
                onClick={(e) => handleAction(e, () => onDelete && onDelete())}
                className="w-full text-left px-3 py-2 rounded-lg text-xs font-semibold text-red-500 hover:bg-red-50 hover:text-red-600 flex items-center gap-2 transition-colors"
              >
                <i className="fa-solid fa-trash w-4 text-center"></i> Delete Folder
              </button>
            )}

            <div className="h-px bg-gray-100 my-1"></div>

            {/* Color Picker */}
            <div className="px-3 py-2">
              <p className="text-[10px] uppercase font-bold text-gray-400 mb-2">Color</p>
              <div className="grid grid-cols-6 gap-2">
                {availableColors.map((c) => (
                  <button
                    key={c}
                    onClick={(e) => handleAction(e, () => onColorChange && onColorChange(c))}
                    className={`w-4 h-4 rounded-full ${styles[c].pickerBg} border border-gray-100 hover:scale-110 transition-transform ${
                      activeColorKey === c ? `ring-2 ${styles[c].ring} ring-offset-1` : ''
                    }`}
                    title={c.charAt(0).toUpperCase() + c.slice(1)}
                  />
                ))}
              </div>
            </div>

          </div>
        )}
      </div>

    </div>
  );
};

export default FolderCard;